import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { usePrevious } from 'react-use';
import { addDefaultMaterial, getDefaultMaterial } from '../../../../../helpers/idb';
import { base64ToBlob, wait } from '../../../../../helpers/jsHelper';
import { setUnityObjectImagesProcessing } from '../../../../../redux/slicers/admin/curatorLoaderSlicer';
import { initialState } from '../../../../../redux/slicers/admin/curatorSlicer';
import {
  selectHasObjectProperties,
  selectSelectedObjectMaterialId,
  selectSelectedObjectName,
  selectUnitySelectedObjectInfoRaw,
} from '../../../../../redux/slicers/admin/curatorUnityObjectSlicer';
import { getAppState } from '../../../../../redux/store';
import { useUnityContext } from '../../../../container/unityContainer';
import { LogConsole } from '../../../../../helpers/logEvents';

export const useLoadDefaultTextures = () => {
  const { materialModule } = useUnityContext();

  const loadDefaultTextures = async ({ object, materialId }) => {
    const textureNameList = [
      object.diffuseName,
      object.normalMapName,
      object.aoMapName,
      object.roughnessMapName,
      object.metallicMapName,
      object.transparencyMapName,
    ].filter(Boolean);

    const textureDataList = textureNameList
      .map((textureName) => {
        const key = getDefaultTextureKey({ materialId, textureName });
        const textureData = defaultTexures[key];
        if (!textureData) return;
        return { key, textureName, data: textureData };
      })
      .filter(Boolean);

    const hasDataForAllTextures = textureDataList.length === textureNameList.length;

    if (!hasDataForAllTextures) {
      // load data from unity
      setUnityObjectImagesProcessing(true);
      wait(10); // small timeout as without it unity blocks javascript and loader does not have time to appear
      materialModule.OnCallBase64ImageDataReceiver();
      return;
    }

    // load data from cache -> simulates loading data from unity
    await Promise.all(
      textureDataList.map(async ({ key, textureName, data }) => {
        window.ReceiveBase64String(
          data.materialId,
          object.objectName,
          'TRIGGERED_FROM_REACT', // data.mapType,
          '', // this should be imageUrl but for now let's not send it as it seems to be working without it
          textureName
        );
      })
    );
  };

  return loadDefaultTextures;
};

export const useGetDefaultObjectMaps = ({ active }) => {
  // requests default object images from unity, we should fetch it before applying any map/texture
  const hasObjectProperties = useSelector(selectHasObjectProperties);
  const objectName = useSelector(selectSelectedObjectName);
  const materialId = useSelector(selectSelectedObjectMaterialId);
  const loadDefaultTextures = useLoadDefaultTextures();
  const prevObjectName = usePrevious(objectName);
  const prevMaterialId = usePrevious(materialId);

  const clearDefaultTextures = (keyBase) => {
    const keys = Object.keys(defaultTexures).filter((key) => key.startsWith(keyBase));
    keys.forEach((key) => {
      const data = defaultTexures[key];
      if (!isRetailAutomationScreen()) {
        URL.revokeObjectURL(data.imageUrl);
        defaultTexures[key].imageUrl = undefined;
      }
    });
  };

  useEffect(() => {
    if (!prevObjectName) return;
    // delte object urls to free memory -> next time we'll get it from indexdb
    const keyBase = getDefaultTextureKeyBase();
    // clearDefaultTextures(keyBase);
  }, [objectName]);

  useEffect(() => {
    if (!prevMaterialId) return;
    if (prevObjectName !== objectName) return;
    const keyBase = getDefaultTextureKeyPrefix({ materialId: prevMaterialId });
    // clearDefaultTextures(keyBase);
    // removeMaterialUrlFromObjectUrlStore(prevMaterialId) // we can do it only if we keep track of all materials in which objects which material is used
  }, [materialId]);

  useEffect(() => {
    if (!active) return;
    if (!hasObjectProperties) return;
    const object = selectUnitySelectedObjectInfoRaw(getAppState());
    // if (materialId) {
    loadDefaultTextures({ object, materialId });
    // }
  }, [objectName, materialId, active]);
};

export const defaultTexures = {}; // map { [`objectName_mapType`]: [...args] }
window.defaultTexures = defaultTexures;

export const getDefaultTextureFromIdb = ({ textureName, materialId }) => {
  const key = getDefaultTextureKey({ textureName, materialId });
  return getDefaultMaterial(key);
};

export const getDefaultTexture = async ({ textureName, materialId }) => {
  const key = getDefaultTextureKey({ textureName, materialId });
  if (defaultTexures[key] && !defaultTexures[key]?.imageUrl) {
    const imageData = await getDefaultMaterial(key);
    defaultTexures[key].imageUrl = imageData?.file ? URL.createObjectURL(imageData?.file) : '';
  }
  return defaultTexures[key];
};

export const getDefaultTextureImageUrl = async ({ textureName, materialId }) => {
  const data = await getDefaultTexture({ textureName, materialId });
  return data?.imageUrl;
};

export const isRetailAutomationScreen = () => {
  return window.location.href.includes('retail-automation');
};

export const cacheDefaultTexture = async ({ materialId, textureName, imageData }) => {
  const key = getDefaultTextureKey({ materialId, textureName });

  let imageUrl = '';

  // If fresh imageData is provided, use it
  if (imageData) {
    try {
      const imageBlob = base64ToBlob({ imageString: imageData });

      // Save to cache
      await addDefaultMaterial({
        file: imageBlob,
        key,
        materialId,
        textureName,
      });

      imageUrl = URL.createObjectURL(imageBlob);
    } catch (err) {
      LogConsole('❌ Failed to create image from imageData:', err);
    }
  } else {
    // Fallback: try to get from cached local store
    try {
      const imageDataLocal = await getDefaultMaterial(key);

      if (imageDataLocal?.file instanceof Blob) {
        imageUrl = URL.createObjectURL(imageDataLocal.file);
      } else {
        LogConsole('⚠️ Cached image file is missing or invalid for key:', key);
      }
    } catch (err) {
      LogConsole('❌ Failed to load cached image data:', err);
    }
  }

  // Update global cache
  defaultTexures[key] = {
    imageUrl,
    textureName,
    materialId,
  }; // store data, we will use it next time instead of calling unity as unity blocks thread when it generates images
  return defaultTexures[key];
};



// export const cacheDefaultTexture = async ({ materialId, textureName, imageData }) => {
//   const key = getDefaultTextureKey({ materialId, textureName });
//   let imageUrl;
//   if (!defaultTexures[key]) {
//     const imageBlob = base64ToBlob({
//       imageString: imageData,
//     });
//     await addDefaultMaterial({
//       file: imageBlob,
//       key,
//       materialId,
//       textureName,
//     });
//     imageUrl = URL.createObjectURL(imageBlob);
//   } else {
//     const imageDataLocal = await getDefaultMaterial(key);
//     imageUrl = imageDataLocal?.file ? URL.createObjectURL(imageDataLocal?.file) : '';
//     if (imageData && isRetailAutomationScreen()) {
//       const imageBlob = base64ToBlob({
//         imageString: imageData,
//       });
//       imageUrl = URL.createObjectURL(imageBlob);
//     }
//   }
//   defaultTexures[key] = {
//     imageUrl,
//     textureName,
//     materialId,
//   }; // store data, we will use it next time instead of calling unity as unity blocks thread when it generates images

//   return defaultTexures[key];
// };

export const getDefaultTextureKey = ({ materialId, textureName }) => {
  const prefix = getDefaultTextureKeyPrefix({ materialId });
  const key = `${prefix}__${textureName || 'NONAME'}`;
  return key;
};

export const getDefaultTextureKeyPrefix = ({ materialId }) => {
  const base = getDefaultTextureKeyBase();
  materialId = materialId || 'DEFAULT';
  return `${base}__${materialId}`;
};

export const getDefaultTextureKeyBase = () => {
  const uuid = initialState.uuid;
  return `${uuid}__`;
};

export const getKeysByUuid = (uuid) => {
  return Object.keys(defaultTexures).filter((key) => key.startsWith(uuid));
};
